<template>
  <div class="report-page">
    <div class="dropdown-container">
      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>

        <select
          id="distributorSelect"
          v-model="selectedDistributorId"
          @change="onDistributorChange"
          :disabled="isDistributorLocked"
        >
          <option value="">Select Distributor</option>

          <option v-if="isAsmUser || isFsmUser || isOtherUser" :value="ALL">
            All Distributors
          </option>

          <option
            v-for="distributor in distributorList"
            :key="distributor.profile_id"
            :value="String(distributor.profile_id)"
          >
            {{
              distributor.business_name ||
              distributor.business_display_name ||
              distributor.name ||
              "Distributor #" + distributor.profile_id
            }}
          </option>
        </select>
      </div>

      <!-- Dealer Category -->
      <div class="dropdown-item">
        <label for="dealerCategorySelect"> Dealer Category </label>

        <select
          id="dealerCategorySelect"
          v-model="selectedCategoryType"
          @change="onCategoryTypeChange"
          :disabled="!selectedDistributorId || loadingFilters"
        >
          <option value="">Select Dealer Category</option>

          <option :value="ALL_CATEGORIES">All Categories</option>

          <option value="individual">Individual</option>

          <option value="group">Group</option>
        </select>
      </div>

      <!-- Group Branch -->
      <div
        v-if="selectedCategoryType === 'group' && selectedDistributorId"
        class="dropdown-item"
      >
        <label for="dealerGroupSelect"> Dealer Group </label>

        <select
          id="dealerGroupSelect"
          v-model="selectedDealerGroupId"
          @change="onDealerGroupChange"
          :disabled="loadingDealerGroups || loadingFilters"
        >
          <option value="">
            {{
              loadingDealerGroups
                ? "Loading Dealer Groups..."
                : "Select Dealer Group"
            }}
          </option>

          <option :value="ALL_GROUPS">All Dealer Groups</option>

          <option
            v-for="group in filteredDealerGroups"
            :key="group.group_id"
            :value="String(group.group_id)"
          >
            {{ group.group_name }}
          </option>
        </select>
      </div>

      <!-- Individual: Dealer Type -->
      <div
        v-if="selectedCategoryType === 'individual' && selectedDistributorId"
        class="dropdown-item"
      >
        <label for="dealerTypeSelect"> Dealer Type </label>

        <select
          id="dealerTypeSelect"
          v-model="selectedDealerCategoryId"
          @change="onDealerTypeChange"
          :disabled="loadingDealerTypes || loadingFilters"
        >
          <option value="">
            {{
              loadingDealerTypes
                ? "Loading Dealer Types..."
                : "Select Dealer Type"
            }}
          </option>

          <option :value="ALL_TYPES">All Dealer Types</option>

          <option
            v-for="category in dealerCategoryList"
            :key="category.id"
            :value="String(category.id)"
          >
            {{ category.dealer_cat_name }}
          </option>
        </select>
      </div>

      <!-- Individual Branch: Nature of Business -->
      <div
        v-if="selectedCategoryType === 'individual' && selectedDealerCategoryId && selectedDealerCategoryId !== ALL_TYPES"
        class="dropdown-item"
      >
        <label for="natureOfBusinessSelect"> Nature of Business </label>

        <select
          id="natureOfBusinessSelect"
          v-model="selectedNatureOfBusinessId"
          @change="onNatureOfBusinessChange"
          :disabled="loadingNatureOfBusiness || loadingFilters"
        >
          <option value="" disabled>
            {{
              loadingNatureOfBusiness
                ? "Loading Nature of Business..."
                : "Select Nature of Business"
            }}
          </option>

          <option :value="ALL_NATURE">All Nature of Business</option>

          <option
            v-for="nature in natureOfBusinessList"
            :key="nature.id"
            :value="String(nature.id)"
          >
            {{ nature.business_name }}
          </option>
        </select>
      </div>

      <!-- Dealer Dropdown -->
      <div v-if="shouldShowDealerDropdown" class="dropdown-item">
        <label for="dealerSelect"> Dealer </label>

        <select
          id="dealerSelect"
          v-model="selectedDealerId"
          @change="onDealerChange"
          :class="{ 'inactive-select': selectedDealerIsInactive }"
          :disabled="loadingDealers || loadingFilters"
        >
          <option value="">
            {{ loadingDealers ? "Loading Dealers..." : "Select Dealer" }}
          </option>

          <option v-if="dealerList.length > 0" :value="ALL">All Dealers</option>

          <option
            v-for="dealer in dealerList"
            :key="dealer.profile_id"
            :value="String(dealer.profile_id)"
            :class="{ 'inactive-option': !dealer.is_active }"
          >
            {{
              dealer.business_display_name ||
              dealer.business_name ||
              dealer.name ||
              "Dealer #" + dealer.profile_id
            }}
          </option>
        </select>
      </div>

      <!-- Start Date -->
      <div class="dropdown-item">
        <label>Start Date</label>

        <input
          type="date"
          v-model="startDate"
          :max="currentDate"
          @change="handleDateChange"
          @blur="validateStartDate(true)"
        />
      </div>

      <!-- End Date -->
      <div class="dropdown-item">
        <label>End Date</label>

        <input
          type="date"
          v-model="endDate"
          :max="currentDate"
          @change="handleDateChange"
          @blur="validateEndDate(true)"
        />
      </div>

      <!-- Search & Export -->
      <div class="search-container">
        <button type="button"
          @click="performSearch(1)"
          class="btn button-default"
          :disabled="
            loading ||
            loadingFilters ||
            loadingDealerTypes ||
            loadingDealerGroups ||
            loadingNatureOfBusiness ||
            loadingDealers
          "
        >
          <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
          {{ loading ? "Searching..." : "Search" }}
        </button>

        <button type="button"
          @click="exportToExcel"
          class="btn button-export"
          :disabled="!reportData.length"
        >
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i>
      Loading...
    </div>

    <!-- Table -->
    <div v-if="!loading && reportData.length" class="card mt-3">
      <div class="table-scroll">
        <table
          class="table table-striped table-bordered"
          :style="{ '--docno-w': docNoWidth }"
        >
          <thead>
            <tr class="header-row-1">
              <th class="col-date">Date</th>

              <th class="col-doc">Document Type</th>

              <th class="col-doc">Dealer</th>

              <th class="col-docno">Document Number</th>

              <th class="col-details">Document Details</th>

              <th class="col-amt text-end">Invoice Amount</th>

              <th class="col-amt text-end">Paid Amount</th>

              <th class="col-amt text-end">Balance Amount</th>

              <th class="col-cumulative text-end">Cumulative Balance Amount</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, index) in cumulativeData" :key="index" :class="{ 'inactive-row': row.dealer_is_active != null && Number(row.dealer_is_active) === 0,}">
              <td class="col-date-cell">
                {{ row.date || "-" }}
              </td>

              <td class="col-doc-cell">
                {{ row.document || "-" }}
              </td>

              <td class="col-doc-cell">
                {{ row.business_name || "-" }}
              </td>

              <td class="col-docno-cell" :title="row.doc_no || ''">
                {{ row.doc_no || "-" }}
              </td>

              <td class="col-details-cell">
                {{ formatDocDetails(row.doc_details) || "-" }}
              </td>

              <td class="col-amt-cell text-end">
                {{ formatNumber(row.invoice_amount) }}
              </td>

              <td class="col-amt-cell text-end">
                {{ formatNumber(row.paid_amount) }}
              </td>

              <td
                class="col-amt-cell text-end"
                :style="{
                  color: Number(row.balance_amount) > 0 ? 'red' : '#212529',
                }"
              >
                {{ formatBalanceNumber(row.balance_amount) }}
              </td>

              <td
                class="col-cumulative-cell text-end"
                :style="{
                  color: Number(row.cumulative_balance) > 0 ? 'red' : '#212529',
                }"
              >
                {{ formatBalanceNumber(row.cumulative_balance) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>



    <div
      v-if="!loading && searchedOnce && !reportData.length"
      class="no-data-message"
    >
      <p>No data found. Please select filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";

import {
  fetchDistributors,
  fetchDealerStatementReport,
  getDealerCategories,
  getDealerGroups,
  getDealersByGroup,
  getNatureOfBusinessByDistributorDealerCategory,
  getDealersByDistributorCategoryNature,
} from "@/services/dataService";

export default {
  name: "DealerStatementForm",

  data() {
    const currentDate = new Date()
      .toISOString()
      .split("T")[0];

    const ALL = "__all__";
    const ALL_NATURE = "__all_nature__";
    const ALL_CATEGORIES = "all";
    const ALL_TYPES = "__all_types__";
    const ALL_GROUPS = "__all_groups__";

    return {
      token:
        localStorage.getItem("token_set") ||
        "",

      profileId: Number(
        localStorage.getItem("profile_id") ||
          0,
      ),

      myProfileId: Number(
        localStorage.getItem("profile_id") ||
          0,
      ),

      currentDate,
      startDate: currentDate,
      endDate: currentDate,

      loading: false,
      loadingFilters: false,
      loadingDealerTypes: false,
      loadingNatureOfBusiness: false,
      loadingDealerGroups: false,
      loadingDealers: false,

      searchedOnce: false,
      redirecting: false,

      userLabel: "",
      roleKey: "OTHER",
      isDistributorBackOffice: false,

      distributorList: [],
      selectedDistributorId: "",
      isDistributorLocked: false,

      selectedCategoryType: "",

      /*
       * Group branch
       */
      dealerGroupList: [],
      filteredDealerGroups: [],
      selectedDealerGroupId: "",

      /*
       * Individual branch
       */
      dealerCategoryList: [],
      selectedDealerCategoryId: "",

      natureOfBusinessList: [],
      selectedNatureOfBusinessId: "",

      /*
       * Final dealer dropdown
       */
      dealerList: [],
      selectedDealerId: "",
      distDealerCache: [],

      reportData: [],

      ALL,
      ALL_NATURE,
      ALL_CATEGORIES,
      ALL_TYPES,
      ALL_GROUPS,

      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      totalPages: 0,
      hasMore: false,

      pageSizeOptions: [
        20,
        50,
        100,
      ],

      jumpToPageInput: "",

      cachedTotals: null,
      isExporting: false,
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    isAsmUser() {
      return this.roleKey === "ASM";
    },

    isFsmUser() {
      return this.roleKey === "FSM";
    },

    isOtherUser() {
      return this.roleKey === "OTHER";
    },

    shouldShowDealerDropdown() {
      if (!this.selectedDistributorId) return false;
      if (this.selectedCategoryType === this.ALL_CATEGORIES) return true;
      
      if (
        this.selectedCategoryType ===
        "individual"
      ) {
        return (
          Boolean(this.selectedDealerCategoryId) &&
          (this.selectedDealerCategoryId === this.ALL_TYPES ||
          Boolean(this.selectedNatureOfBusinessId))
        );
      }

      if (
        this.selectedCategoryType ===
        "group"
      ) {
        return Boolean(this.selectedDealerGroupId);
      }

      return false;
    },

    cumulativeData() {
      return this.reportData.map(
        (row) => ({
          ...row,

          cumulative_balance:
            Number(
              row.running_cumulative,
            ) || 0,
        }),
      );
    },

    visiblePageNumbers() {
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        return Array.from(
          {
            length: total,
          },
          (_, index) => index + 1,
        );
      }

      const pages = [1];

      let start = Math.max(
        2,
        current - 2,
      );

      let end = Math.min(
        total - 1,
        current + 2,
      );

      if (current <= 4) {
        start = 2;
        end = 5;
      }

      if (
        current >=
        total - 3
      ) {
        start = total - 4;
        end = total - 1;
      }

      if (start > 2) {
        pages.push("...");
      }

      for (
        let page = start;
        page <= end;
        page += 1
      ) {
        pages.push(page);
      }

      if (end < total - 1) {
        pages.push("...");
      }

      pages.push(total);

      return pages;
    },

    rowRangeText() {
      if (!this.totalRows) {
        return "";
      }

      const start =
        (this.currentPage - 1) *
          this.perPage +
        1;

      const end = Math.min(
        this.currentPage *
          this.perPage,
        this.totalRows,
      );

      return `Showing ${start}–${end} of ${this.totalRows.toLocaleString()} rows`;
    },

    selectedDealerIsInactive() {
      const dealer = this.dealerList.find(
        (d) => String(d.profile_id) === String(this.selectedDealerId),
      );
      return dealer ? !dealer.is_active : false;
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    toast(type, message) {
      const toast = useToast();

      if (type === "error") {
        toast.error(message, {
          position: "top-right",
          timeout: 3000,
        });

        return;
      }

      toast.success(message, {
        position: "top-right",
        timeout: 3000,
      });
    },

    bodyOf(value) {
      return value?.data ?? value;
    },

    listOf(body) {
      if (Array.isArray(body)) {
        return body;
      }

      if (
        Array.isArray(body?.data)
      ) {
        return body.data;
      }

      if (
        Array.isArray(
          body?.data?.data,
        )
      ) {
        return body.data.data;
      }

      if (
        Array.isArray(body?.result)
      ) {
        return body.result;
      }

      return [];
    },

    isSuccessResponse(body) {
      const status = body?.status;

      return (
        status === true ||
        status === 1 ||
        status === "1" ||
        String(status).toLowerCase() ===
          "true" ||
        String(status).toLowerCase() ===
          "success"
      );
    },

    msgOf(value) {
      const body = this.bodyOf(
        value?.response?.data ??
          value,
      );

      return String(
        body?.message ??
          body?.error ??
          value?.message ??
          value?.response?.data
            ?.message ??
          "",
      );
    },

    isUnauthorized(value) {
      const httpStatus =
        value?.response?.status ??
        value?.status;

      const message = this.msgOf(
        value,
      ).toLowerCase();

      return (
        httpStatus === 401 ||
        httpStatus === 419 ||
        httpStatus === 440 ||
        httpStatus === 498 ||
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/.test(
          message,
        )
      );
    },

    handleUnauthorized(value) {
      if (
        !this.isUnauthorized(value)
      ) {
        return false;
      }

      this.redirectToLogin();

      return true;
    },

    redirectToLogin() {
      if (this.redirecting) {
        return;
      }

      this.redirecting = true;

      this.toast(
        "error",
        "Session expired. Please login again.",
      );

      localStorage.removeItem(
        "token_set",
      );

      localStorage.removeItem(
        "profile_id",
      );

      localStorage.removeItem(
        "user_label",
      );

      localStorage.removeItem(
        "userLabel",
      );

      localStorage.removeItem(
        "role",
      );

      if (this.$router) {
        this.$router.replace(
          "/login",
        );
      } else {
        window.location.replace(
          "/login",
        );
      }
    },

    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem(
          "user_label",
        ) ||
        localStorage.getItem(
          "userLabel",
        ) ||
        localStorage.getItem(
          "role",
        ) ||
        "";

      this.userLabel = String(
        labelFromStorage,
      );

      const normalized =
        this.userLabel
          .toUpperCase()
          .trim();

      this.isDistributorBackOffice =
        normalized ===
        "DISTRIBUTOR BACK OFFICE";

      if (
        normalized ===
          "DISTRIBUTOR" ||
        normalized.startsWith(
          "DISTRIBUTOR",
        ) ||
        normalized === "DIS" ||
        normalized.startsWith(
          "DIS_",
        )
      ) {
        this.roleKey = "DIS";
      } else if (
        normalized === "ASM" ||
        normalized.startsWith(
          "ASM_",
        )
      ) {
        this.roleKey = "ASM";
      } else if (
        normalized === "FSM" ||
        normalized.startsWith(
          "FSM_",
        )
      ) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    resetPagination() {
      this.currentPage = 1;
      this.totalRows = 0;
      this.totalPages = 0;
      this.hasMore = false;
      this.jumpToPageInput = "";
      this.cachedTotals = null;
    },

    clearReport() {
      this.reportData = [];
      this.searchedOnce = false;

      this.currentPage = 1;
      this.totalRows = 0;
      this.totalPages = 0;
      this.hasMore = false;
      this.jumpToPageInput = "";
      this.cachedTotals = null;
    },

    mapDealerRow(dealer) {
      const profileId =
        dealer?.profile_id ?? "";

      const name =
        dealer
          ?.business_display_name ||
        dealer?.business_name ||
        dealer?.name ||
        dealer?.dealer_name ||
        dealer?.profile_name ||
        "";

      const isActive = dealer?.is_active == null ? true : Number(dealer.is_active) !== 0;

      return {
        ...dealer,

        profile_id:
          String(profileId),

        business_display_name:
          name,

        business_name:
          dealer?.business_name ||
          name,

        name,
        is_active: isActive,
      };
    },

    async init() {
      this.loading = true;

      await this.$nextTick();

      try {
        this.token =
          localStorage.getItem(
            "token_set",
          ) || "";

        if (!this.token) {
          this.redirectToLogin();
          return;
        }

        this.profileId = Number(
          localStorage.getItem(
            "profile_id",
          ) || 0,
        );

        this.initRoleFromStorage();

        /*
         * Distributor back office users report to a distributor,
         * so the locked distributor is their reporting profile,
         * not their own profile.
         */
        this.myProfileId =
          this.isDistributorBackOffice
            ? Number(
                localStorage.getItem(
                  "reportingL1ProfileId",
                ),
              ) || this.profileId
            : this.profileId;

        await this.loadDistributors();
      } finally {
        this.loading = false;
      }
    },

    async loadDistributors() {
      try {
        const response =
          await fetchDistributors(
            this.profileId,
            this.token,
          );

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        const body =
          this.bodyOf(response);

        this.distributorList =
          this.listOf(body);

        if (
          this.isDistributorUser &&
          this.myProfileId
        ) {
          const ownDistributor =
            this.distributorList.find(
              (item) =>
                String(
                  item.profile_id,
                ) ===
                String(
                  this.myProfileId,
                ),
            );

          /*
           * Keep the locked dropdown from rendering blank when
           * the list does not contain the user's distributor.
           */
          if (!ownDistributor) {
            const reportingName =
              `${localStorage.getItem("reportingL1FirstName") || ""} ${localStorage.getItem("reportingL1LastName") || ""}`.trim();

            this.distributorList = [
              ...this.distributorList,
              {
                profile_id:
                  this.myProfileId,

                business_name:
                  this
                    .isDistributorBackOffice
                    ? reportingName
                    : localStorage.getItem(
                        "business_name",
                      ) || "",
              },
            ];
          }

          this.selectedDistributorId =
            String(
              ownDistributor
                ?.profile_id ||
                this.myProfileId,
            );

          this.isDistributorLocked =
            true;

          await this.onDistributorChange();
        } else {
          this.selectedDistributorId =
            "";

          this.isDistributorLocked =
            false;
        }
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        console.error(
          "Distributor load error:",
          error,
        );

        this.toast(
          "error",
          "Failed to load distributor list.",
        );
      }
    },

    async onDistributorChange() {
      this.selectedCategoryType =
        "";

      this.selectedDealerGroupId =
        "";

      this.dealerGroupList = [];
      this.filteredDealerGroups =
        [];

      this.dealerCategoryList = [];

      this.selectedDealerCategoryId =
        "";

      this.natureOfBusinessList =
        [];

      this.selectedNatureOfBusinessId =
        "";

      this.dealerList = [];
      this.selectedDealerId = "";
      this.distDealerCache = [];

    },

    async onCategoryTypeChange() {
      this.selectedDealerGroupId =
        "";

      this.dealerGroupList = [];
      this.filteredDealerGroups =
        [];

      this.dealerCategoryList = [];

      this.selectedDealerCategoryId =
        "";

      this.natureOfBusinessList =
        [];

      this.selectedNatureOfBusinessId =
        "";

      this.dealerList = [];
      this.selectedDealerId = "";
      this.distDealerCache = [];

      if (this.selectedCategoryType === this.ALL_CATEGORIES) {
        await this.loadIndividualDealers();
        return;
      }

      if (
        this.selectedCategoryType ===
        "group"
      ) {
        await this.loadDealerGroupsForDistributor();

        return;
      }

      if (
        this.selectedCategoryType ===
        "individual"
      ) {
        await this.loadDealerCategories();
      }
    },

    async loadDealerCategories() {
      this.loadingDealerTypes =
        true;

      try {
        const response =
          await getDealerCategories(
            this.token,
          );

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        const body =
          this.bodyOf(response);

        if (
          !this.isSuccessResponse(
            body,
          )
        ) {
          this.dealerCategoryList =
            [];

          this.toast(
            "error",
            body?.message ||
              "Failed to load dealer types.",
          );

          return;
        }

        this.dealerCategoryList =
          this.listOf(body).map(
            (category) => ({
              id: String(
                category.id,
              ),

              dealer_cat_name:
                category
                  .dealer_cat_name ||
                category.name ||
                category
                  .category_name ||
                `Category #${category.id}`,
            }),
          );

        if (
          !this.dealerCategoryList
            .length
        ) {
          this.toast(
            "error",
            "No dealer types found.",
          );
        }
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        console.error(
          "Dealer type load error:",
          error,
        );

        this.dealerCategoryList =
          [];

        this.toast(
          "error",
          "Failed to load dealer types.",
        );
      } finally {
        this.loadingDealerTypes =
          false;
      }
    },

    async loadDealerGroupsForDistributor() {
      this.loadingDealerGroups =
        true;

      try {
        const distributorId =
          this.selectedDistributorId ===
          this.ALL
            ? this.profileId
            : this.selectedDistributorId;

        const response =
          await getDealerGroups(
            distributorId,
            this.token,
          );

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        const body =
          this.bodyOf(response);

        if (
          !this.isSuccessResponse(
            body,
          )
        ) {
          this.dealerGroupList =
            [];

          this.filteredDealerGroups =
            [];

          this.toast(
            "error",
            body?.message ||
              "Failed to load dealer groups.",
          );

          return;
        }

        this.dealerGroupList =
          this.listOf(body)
            .map((group) => ({
              group_id:
                group.group_id ||
                group.id,

              group_name:
                group.group_name ||
                group.name ||
                "-",

              distributor_id:
                group.distributor_id,

              dealers_count:
                group.dealers_count,
            }))
            .filter(
              (group) =>
                group.group_id,
            );

        this.filteredDealerGroups =
          [
            ...this
              .dealerGroupList,
          ];

        if (
          !this.filteredDealerGroups
            .length
        ) {
          this.toast(
            "error",
            "No dealer groups found.",
          );
        }
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        console.error(
          "Dealer group load error:",
          error,
        );

        this.dealerGroupList = [];
        this.filteredDealerGroups =
          [];

        this.toast(
          "error",
          "Failed to load dealer groups.",
        );
      } finally {
        this.loadingDealerGroups =
          false;
      }
    },

    async onDealerGroupChange() {
      this.selectedDealerId = "";
      this.dealerList = [];
      this.distDealerCache = [];

      if (!this.selectedDealerGroupId) return;

      await this.loadGroupBranchDealers();
    },

    async loadGroupBranchDealers() {
      if (
        !this.selectedDealerGroupId
      ) {
        this.dealerList = [];
        return;
      }

      this.loadingDealers = true;
      this.loadingFilters = true;

      try {
        const isAllGroups = this.selectedDealerGroupId === this.ALL_GROUPS;
        const groupId = isAllGroups ? "all" : Number(this.selectedDealerGroupId);

        const response = await getDealersByGroup(groupId, this.token, {
          status: true,
          ...(isAllGroups && {
            user_profile_id: this.profileId,
            distributor_profile_id:
              this.selectedDistributorId === this.ALL ? "" : this.selectedDistributorId,
          }),
        });

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        const body =
          this.bodyOf(response);

        if (
          !this.isSuccessResponse(
            body,
          )
        ) {
          this.dealerList = [];
          this.distDealerCache = [];

          this.toast(
            "error",
            body?.message ||
              "Failed to load dealers for selected group.",
          );

          return;
        }

        const dealers =
          this.listOf(body)
            .map((dealer) =>
              this.mapDealerRow(
                dealer,
              ),
            )
            .filter((dealer) =>
              Boolean(
                dealer.profile_id,
              ),
            );

        this.distDealerCache = [
          ...dealers,
        ];

        this.dealerList = [
          ...dealers,
        ];

        if (
          !this.dealerList.length
        ) {
          this.toast(
            "error",
            "No dealers found for this group.",
          );
        }
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        console.error(
          "Group dealer load error:",
          error,
        );

        this.dealerList = [];
        this.distDealerCache = [];

        this.toast(
          "error",
          "Failed to load dealers for selected group.",
        );
      } finally {
        this.loadingDealers = false;
        this.loadingFilters = false;
      }
    },

    async onDealerTypeChange() {
      this.natureOfBusinessList =
        [];

      this.selectedNatureOfBusinessId =
        "";

      this.dealerList = [];
      this.selectedDealerId = "";
      this.distDealerCache = [];

      if (!this.selectedDealerCategoryId) return;

      if (this.selectedDealerCategoryId === this.ALL_TYPES) {
        await this.loadIndividualDealers();
        return;
      }

      /*
       * Load only the Nature of Business options.
       *
       * Do not automatically load dealers.
       * User must explicitly select either:
       * - All Nature of Business
       * - A specific Nature of Business
       */
      await this.loadNatureOfBusiness();
    },

    async loadNatureOfBusiness() {
      if (
        !this.selectedDistributorId ||
        !this.selectedDealerCategoryId
      ) {
        this.natureOfBusinessList =
          [];

        return;
      }

      this.loadingNatureOfBusiness =
        true;

      try {
        const distributorProfileId =
          this.selectedDistributorId ===
          this.ALL
            ? ""
            : this.selectedDistributorId;

        const payload = {
          user_profile_id:
            this.profileId,

          distributor_profile_id:
            distributorProfileId,

          dealer_cat_id: Number(
            this.selectedDealerCategoryId,
          ),
        };

        const response =
          await getNatureOfBusinessByDistributorDealerCategory(
            payload,
            this.token,
          );

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        const body =
          this.bodyOf(response);

        if (
          !this.isSuccessResponse(
            body,
          )
        ) {
          this.natureOfBusinessList =
            [];

          this.toast(
            "error",
            body?.message ||
              "Failed to load Nature of Business.",
          );

          return;
        }

        this.natureOfBusinessList =
          this.listOf(body)
            .map((nature) => ({
              id: String(
                nature.id,
              ),

              dealer_type_id:
                String(
                  nature
                    .dealer_type_id ||
                    "",
                ),

              business_name:
                nature.business_name ||
                nature.businesses_name ||
                "",
            }))
            .filter(
              (nature) =>
                nature.id &&
                nature.business_name,
            );

        if (
          !this.natureOfBusinessList
            .length
        ) {
          this.toast(
            "error",
            "No active Nature of Business records found.",
          );
        }
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        console.error(
          "Nature of Business load error:",
          error,
        );

        this.natureOfBusinessList =
          [];

        this.toast(
          "error",
          "Failed to load Nature of Business.",
        );
      } finally {
        this.loadingNatureOfBusiness =
          false;
      }
    },

    async onNatureOfBusinessChange() {
      this.dealerList = [];
      this.selectedDealerId = "";
      this.distDealerCache = [];

      /*
       * Empty string is only the placeholder:
       * "Select Nature of Business".
       */
      if (
        !this.selectedDealerCategoryId ||
        !this.selectedNatureOfBusinessId
      ) {
        return;
      }

      await this.loadIndividualDealers();
    },

    async loadIndividualDealers() {
      const isAllCategories = this.selectedCategoryType === this.ALL_CATEGORIES;
      const isAllTypes =
        this.selectedCategoryType === "individual" &&
        this.selectedDealerCategoryId === this.ALL_TYPES;
      const noCategoryFilter = isAllCategories || isAllTypes;

      if (
        !this.selectedDistributorId ||
        (!noCategoryFilter &&
          (!this.selectedDealerCategoryId || !this.selectedNatureOfBusinessId))
      ) {
        this.dealerList = [];
        this.distDealerCache = [];

        return;
      }

      this.loadingDealers = true;
      this.loadingFilters = true;

      try {
        const distributorProfileId =
          this.selectedDistributorId ===
          this.ALL
            ? ""
            : this.selectedDistributorId;

        const payload = {
          user_profile_id:
            this.profileId,

          distributor_profile_id:
            distributorProfileId,

          dealer_cat_id: noCategoryFilter ? "" : Number(this.selectedDealerCategoryId),

          /*
           * All Nature sends an empty value.
           * Specific Nature sends its numeric ID.
           */
          nature_of_business_id:
            noCategoryFilter || this.selectedNatureOfBusinessId === this.ALL_NATURE
              ? ""
              : Number(this.selectedNatureOfBusinessId),
        };

        const response =
          await getDealersByDistributorCategoryNature(
            payload,
            this.token,
          );

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        const body =
          this.bodyOf(response);

        if (
          !this.isSuccessResponse(
            body,
          )
        ) {
          this.dealerList = [];
          this.distDealerCache = [];

          this.toast(
            "error",
            body?.message ||
              "Failed to load dealer list.",
          );

          return;
        }

        const dealers =
          this.listOf(body)
            .map((dealer) =>
              this.mapDealerRow(
                dealer,
              ),
            )
            .filter(
              (dealer) =>
                dealer.profile_id,
            );

        this.distDealerCache = [
          ...dealers,
        ];

        this.dealerList = [
          ...dealers,
        ];

        if (
          !this.dealerList.length
        ) {
          this.toast(
            "error",
            "No dealers found for selected filters.",
          );
        }
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        console.error(
          "Individual dealer load error:",
          error,
        );

        this.dealerList = [];
        this.distDealerCache = [];

        this.toast(
          "error",
          error?.message ||
            "Failed to load dealer list.",
        );
      } finally {
        this.loadingDealers = false;
        this.loadingFilters = false;
      }
    },

    isValidISODate(dateString) {
      if (
        !dateString ||
        typeof dateString !==
          "string"
      ) {
        return false;
      }

      if (
        dateString.includes("/")
      ) {
        return false;
      }

      const match =
        dateString.match(
          /^(\d{4})-(\d{2})-(\d{2})$/,
        );

      if (!match) {
        return false;
      }

      const year =
        Number(match[1]);

      const month =
        Number(match[2]);

      const day =
        Number(match[3]);

      const date = new Date(
        year,
        month - 1,
        day,
      );

      return (
        date.getFullYear() ===
          year &&
        date.getMonth() ===
          month - 1 &&
        date.getDate() === day
      );
    },

    validateStartDate(
      showToast = true,
    ) {
      if (!this.startDate) {
        return true;
      }

      if (
        !this.isValidISODate(
          this.startDate,
        )
      ) {
        if (showToast) {
          this.toast(
            "error",
            "Invalid Start Date",
          );
        }

        this.startDate = "";

        return false;
      }

      if (
        this.startDate >
        this.currentDate
      ) {
        if (showToast) {
          this.toast(
            "error",
            "Start Date cannot be in the future",
          );
        }

        this.startDate = "";

        return false;
      }

      return true;
    },

    validateEndDate(
      showToast = true,
    ) {
      if (!this.endDate) {
        return true;
      }

      if (
        !this.isValidISODate(
          this.endDate,
        )
      ) {
        if (showToast) {
          this.toast(
            "error",
            "Invalid End Date",
          );
        }

        this.endDate = "";

        return false;
      }

      if (
        this.endDate >
        this.currentDate
      ) {
        if (showToast) {
          this.toast(
            "error",
            "End Date cannot be in the future",
          );
        }

        this.endDate = "";

        return false;
      }

      return true;
    },

    handleDateChange() {

      this.validateStartDate(false);
      this.validateEndDate(false);

      if (
        this.startDate &&
        this.endDate &&
        this.endDate <
          this.startDate
      ) {
        this.toast(
          "error",
          "End date cannot be before start date.",
        );

        this.endDate = "";
      }
    },

    validateDatesForSearch() {
      if (!this.startDate) {
        this.toast(
          "error",
          "Please select valid Start Date",
        );

        return false;
      }

      if (!this.endDate) {
        this.toast(
          "error",
          "Please select valid End Date",
        );

        return false;
      }

      if (
        !this.validateStartDate(
          true,
        )
      ) {
        return false;
      }

      if (
        !this.validateEndDate(
          true,
        )
      ) {
        return false;
      }

      if (
        this.endDate <
        this.startDate
      ) {
        this.toast(
          "error",
          "End date cannot be before start date.",
        );

        return false;
      }

      return true;
    },

    validateFiltersOneByOne() {
      const isAllCategories = this.selectedCategoryType === this.ALL_CATEGORIES;
      const isAllTypes = this.selectedCategoryType === "individual" && this.selectedDealerCategoryId === this.ALL_TYPES;
      const isAllGroups = this.selectedCategoryType === "group" && this.selectedDealerGroupId === this.ALL_GROUPS;
      if (
        !this.selectedDistributorId
      ) {
        this.toast(
          "error",
          "Please select Distributor.",
        );

        return false;
      }

      if (
        !this.selectedCategoryType
      ) {
        this.toast(
          "error",
          "Please select Dealer Category.",
        );

        return false;
      }

      if (!isAllCategories) {
        if (this.selectedCategoryType === "group" && !this.selectedDealerGroupId) 
        {
          this.toast("error", "Please select Dealer Group.",);
          return false;
        }

        if (this.selectedCategoryType === "individual" && !this.selectedDealerCategoryId) 
        {
          this.toast("error", "Please select Dealer Type.",);
          return false;
        }

        if (this.selectedCategoryType === "individual" && !isAllTypes && !this.selectedNatureOfBusinessId) 
        {
          this.toast("error", "Please select Nature of Business.",);
          return false;
        }

        if (this.shouldShowDealerDropdown && !this.selectedDealerId) {
          this.toast("error", "Please select Dealer.");
          return false;
        }
      }

      if (!this.startDate) {
        this.toast(
          "error",
          "Please select Start Date.",
        );

        return false;
      }

      if (!this.endDate) {
        this.toast(
          "error",
          "Please select End Date.",
        );

        return false;
      }

      return true;
    },

    formatDocDetails(value) {
      if (!value) return "";

      const counts = new Map();

      String(value)
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => {
          counts.set(item, (counts.get(item) || 0) + 1);
        });

      return Array.from(counts, ([label, count]) =>
        `• ${count > 1 ? `${label}(${count})` : label}`,
      ).join("\n");
    },

    formatNumber(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "0.00";
      }

      const numberValue =
        Number(value);

      if (
        Number.isNaN(
          numberValue,
        )
      ) {
        return "0.00";
      }

      return numberValue.toLocaleString(
        "en-US",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      );
    },

    formatBalanceNumber(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "0.00";
      }

      const numberValue =
        Number(value);

      if (
        Number.isNaN(
          numberValue,
        )
      ) {
        return "0.00";
      }

      if (numberValue > 0) {
        return (
          "-" +
          numberValue.toLocaleString(
            "en-US",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          )
        );
      }

      return Math.abs(
        numberValue,
      ).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async performSearch(
      pageToLoad = 1,
    ) {
      this.token =
        localStorage.getItem(
          "token_set",
        ) || "";

      /*
       * Clear previous table immediately for
       * every fresh Search click.
       */
      if (pageToLoad === 1) {
        this.reportData = [];
        this.currentPage = 1;
        this.totalRows = 0;
        this.totalPages = 0;
        this.hasMore = false;
        this.jumpToPageInput = "";
        this.cachedTotals = null;
        this.searchedOnce = false;
      }

      if (!this.token) {
        this.redirectToLogin();
        return;
      }

      if (
        !this.validateFiltersOneByOne()
      ) {
        return;
      }

      if (
        !this.validateDatesForSearch()
      ) {
        this.searchedOnce = true;

        return;
      }

      this.loading = true;

      try {
        const isAllCategories = this.selectedCategoryType === this.ALL_CATEGORIES;
        const isAllTypes = this.selectedCategoryType === "individual" && this.selectedDealerCategoryId === this.ALL_TYPES;
        const isAllGroups = this.selectedCategoryType === "group" && this.selectedDealerGroupId === this.ALL_GROUPS;
        const skipDealer = isAllCategories || isAllTypes || isAllGroups;

        const payload = {
          start_date:
            this.startDate,

          end_date:
            this.endDate,

          dealer_profile_id:
            !this.selectedDealerId || this.selectedDealerId === this.ALL
              ? null
              : this.selectedDealerId,

          distributor_profile_id:
            this.selectedDistributorId ===
            this.ALL
              ? null
              : this.selectedDistributorId,

          dealer_category_type:
            this.selectedCategoryType,

          dealer_cat_id:
            this.selectedCategoryType ===
            "individual"  && !isAllTypes
              ? Number(
                  this.selectedDealerCategoryId,
                )
              : null,

          /*
           * All Nature sends null to the report API.
           * Specific Nature sends its numeric ID.
           */
          nature_of_business_id:
            this.selectedCategoryType ===
              "individual" && !isAllTypes &&
            this.selectedNatureOfBusinessId !==
              this.ALL_NATURE
              ? Number(
                  this.selectedNatureOfBusinessId,
                )
              : null,

          dealer_group_id:
            this.selectedCategoryType ===
            "group" && !isAllGroups
              ? Number(
                  this.selectedDealerGroupId,
                )
              : null,

          asm_profile_id: null,
          fsm_profile_id: null,

          page: 1,

          per_page: 1000000,
        };

        const apiResponse =
          await fetchDealerStatementReport(
            this.token,
            payload,
          );

        if (
          this.handleUnauthorized(
            apiResponse,
          )
        ) {
          return;
        }

        const response =
          this.bodyOf(apiResponse);

        if (
          this.handleUnauthorized(
            response,
          )
        ) {
          return;
        }

        if (
          !response ||
          response.status !== true
        ) {
          this.reportData = [];
          this.resetPagination();

          this.toast(
            "error",
            response?.message ||
              "Error loading Dealer Statement.",
          );

          return;
        }

        if (
          !Array.isArray(
            response.data,
          ) ||
          response.data.length === 0
        ) {
          this.reportData = [];
          this.currentPage = 1;
          this.totalRows = 0;
          this.totalPages = 0;
          this.hasMore = false;
          this.jumpToPageInput = "";
          this.cachedTotals = null;

          if (pageToLoad === 1) {
            this.toast(
              "error",
              response.message ||
                "No data found for selected filters.",
            );
          }

          return;
        }

        this.reportData =
          response.data;

        const pagination =
          response.pagination ||
          {};

        this.currentPage =
          pagination.current_page ||
          pageToLoad;

        this.totalRows =
          pagination.total || 0;

        this.totalPages =
          pagination.total_pages ||
          Math.ceil(
            (pagination.total || 0) /
              this.perPage,
          ) ||
          1;

        this.hasMore =
          pagination.has_more !==
          undefined
            ? pagination.has_more
            : this.currentPage <
              this.totalPages;

        if (response.totals) {
          this.cachedTotals =
            response.totals;
        }

        this.toast(
          "success",
          "Report loaded successfully.",
        );
      } catch (error) {
        if (
          this.handleUnauthorized(
            error,
          )
        ) {
          return;
        }

        this.reportData = [];
        this.resetPagination();

        console.error(
          error?.response?.data ||
            error,
        );

        this.toast(
          "error",
          error?.response?.data
            ?.message ||
            "Error loading report.",
        );
      } finally {
        this.loading = false;
        this.searchedOnce = true;
      }
    },

    goToPage(page) {
      if (
        typeof page !== "number" ||
        !Number.isFinite(page)
      ) {
        return;
      }

      if (
        page < 1 ||
        page > this.totalPages ||
        page === this.currentPage ||
        this.loading
      ) {
        return;
      }

      this.performSearch(page);
    },

    nextPage() {
      this.goToPage(
        this.currentPage + 1,
      );
    },

    prevPage() {
      this.goToPage(
        this.currentPage - 1,
      );
    },

    firstPage() {
      this.goToPage(1);
    },

    lastPage() {
      this.goToPage(
        this.totalPages,
      );
    },

    onPageSizeChange() {
      this.perPage =
        Number(this.perPage) ||
        20;

      this.reportData = [];
      this.currentPage = 1;
      this.totalRows = 0;
      this.totalPages = 0;
      this.hasMore = false;
      this.jumpToPageInput = "";
      this.cachedTotals = null;

      if (
        this.selectedDistributorId &&
        this.selectedCategoryType &&
        this.selectedDealerId
      ) {
        this.performSearch(1);
      }
    },

    handleJumpToPage() {
      const page = parseInt(
        String(
          this.jumpToPageInput ||
            "",
        ).trim(),
        10,
      );

      if (
        !Number.isFinite(page) ||
        page < 1 ||
        page > this.totalPages
      ) {
        this.toast(
          "error",
          `Enter a page between 1 and ${this.totalPages}.`,
        );

        return;
      }

      this.jumpToPageInput = "";

      this.performSearch(page);
    },

    exportToExcel() {
      if (
        !this.reportData.length
      ) {
        this.toast(
          "error",
          "No data to export.",
        );

        return;
      }

      const getDistributorName =
        () => {
          if (
            !this.selectedDistributorId ||
            this.selectedDistributorId ===
              this.ALL
          ) {
            return "All";
          }

          const distributor =
            this.distributorList.find(
              (item) =>
                String(
                  item.profile_id,
                ) ===
                String(
                  this.selectedDistributorId,
                ),
            );

          return (
            distributor
              ?.business_name ||
            distributor
              ?.business_display_name ||
            distributor?.name ||
            ""
          );
        };

      const getDealerCategoryName =
        () => {
          if (this.selectedCategoryType === this.ALL_CATEGORIES) return "All Categories";

          if (
            this.selectedCategoryType ===
            "group"
          ) {
            return "Group";
          }

          if (
            this.selectedCategoryType ===
            "individual"
          ) {
            return "Individual";
          }

          return "";
        };

      const getDealerTypeName =
        () => {
          if (
            this.selectedCategoryType !==
            "individual"
          ) {
            return "-";
          }

          if (this.selectedDealerCategoryId === this.ALL_TYPES) return "All Dealer Types";

          const category =
            this.dealerCategoryList.find(
              (item) =>
                String(item.id) ===
                String(
                  this.selectedDealerCategoryId,
                ),
            );

          return (
            category
              ?.dealer_cat_name ||
            ""
          );
        };

      const getNatureName = () => {
        if (
          this.selectedCategoryType !==
          "individual"
        ) {
          return "-";
        }

        if (this.selectedDealerCategoryId === this.ALL_TYPES) return "All";

        if (
          this.selectedNatureOfBusinessId ===
          this.ALL_NATURE
        ) {
          return "All Nature of Business";
        }

        if (
          !this.selectedNatureOfBusinessId
        ) {
          return "-";
        }

        const nature =
          this.natureOfBusinessList.find(
            (item) =>
              String(item.id) ===
              String(
                this.selectedNatureOfBusinessId,
              ),
          );

        return (
          nature?.business_name ||
          "-"
        );
      };

      const getDealerGroupName =
        () => {
          if (
            this.selectedCategoryType !==
            "group"
          ) {
            return "-";
          }

          if (this.selectedDealerGroupId === this.ALL_GROUPS) return "All Dealer Groups";

          const selectedGroup =
            this.filteredDealerGroups.find(
              (group) =>
                String(
                  group.group_id,
                ) ===
                String(
                  this.selectedDealerGroupId,
                ),
            );

          return (
            selectedGroup
              ?.group_name ||
            "-"
          );
        };

      const getDealerName = () => {
        if (
          !this.selectedDealerId ||
          this.selectedDealerId ===
            this.ALL
        ) {
          return "All";
        }

        const dealer =
          this.dealerList.find(
            (item) =>
              String(
                item.profile_id,
              ) ===
              String(
                this.selectedDealerId,
              ),
          );

        return (
          dealer
            ?.business_display_name ||
          dealer?.business_name ||
          dealer?.name ||
          ""
        );
      };

      const filterRows = [
        [
          "Dealer Statement Report",
        ],
        [],
        [
          "Distributor",
          getDistributorName(),
        ],
        [
          "Dealer Category",
          getDealerCategoryName(),
        ],
        [
          "Dealer Type",
          getDealerTypeName(),
        ],
        [
          "Nature of Business",
          getNatureName(),
        ],
        [
          "Dealer Group",
          getDealerGroupName(),
        ],
        [
          "Dealer",
          getDealerName(),
        ],
        [
          "Start Date",
          this.startDate,
        ],
        [
          "End Date",
          this.endDate,
        ],
        [],
      ];

      const header = [
        "Date",
        "Document",
        "Dealer",
        "Doc No",
        "Doc. Details",
        "Invoice Amount",
        "Paid Amount",
        "Balance Amount",
        "Cumulative Balance Amount",
      ];

      let cumulative = 0;

      const rows =
        this.reportData.map(
          (row) => {
            const balanceAmount =
              Number(
                row.balance_amount,
              ) || 0;

            const cumulative = Number(row.running_cumulative) || 0;

            return [
              row.date || "",
              row.document || "",
              row.business_name || "",
              row.doc_no || "",
              this.formatDocDetails(row.doc_details) || "",

              Number(
                row.invoice_amount,
              ) || 0,

              Number(
                row.paid_amount,
              ) || 0,

              balanceAmount > 0
                ? -balanceAmount
                : Math.abs(
                    balanceAmount,
                  ),

              cumulative > 0
                ? -cumulative
                : Math.abs(
                    cumulative,
                  ),
            ];
          },
        );

      const firstName =
        localStorage.getItem(
          "first_name",
        ) || "";

      const lastName =
        localStorage.getItem(
          "last_name",
        ) || "";

      const generatedBy =
        `${firstName} ${lastName}`.trim();

      const generatedAt =
        new Date().toLocaleString();

      const generatedRows = [
        [],
        [
          "Generated By",
          generatedBy,
        ],
        [
          "Generated At",
          generatedAt,
        ],
      ];

      const worksheet =
        XLSX.utils.aoa_to_sheet([
          ...filterRows,
          header,
          ...rows,
          ...generatedRows,
        ]);

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Dealer_Statement",
      );

      XLSX.writeFile(
        workbook,
        `Dealer_Statement_${this.startDate}_to_${this.endDate}.xlsx`,
      );

      this.toast(
        "success",
        "Excel exported successfully.",
      );
    },
  },
};
</script>

<style scoped>
.adjust-btn {
  padding: 5px 10px;
  margin-right: 5px;
  border: 1px solid #ccc;
  background: #f5f5f5;
  cursor: pointer;
  border-radius: 4px;
}

.active-plus {
  background-color: #28a745;
  color: white;
}

.active-minus {
  background-color: #dc3545;
  color: white;
}

.qty-input {
  width: 100px;
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.page-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 15px;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}

.page-search-container {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.page-search-container.dropdown-item {
  flex-grow: 0;
  flex-basis: auto;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;
  min-width: 250px;
}

.search-input:hover {
  border-color: #0056b3;
}

.new-item-btn {
  margin-left: auto;
  padding: 10px 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.select-distributor {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  min-width: 250px;
}

.select-reason {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 60%;
  min-width: 200px;
}

/* Keep the rest of your existing dealer screen styles */
.card {
  padding: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-shield {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

i:hover {
  color: #0056b3;
  transform: scale(1.2);
}

i:active {
  transform: scale(1.1);
}

.action-icon {
  margin-right: 10px;
}

.status-label {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 5px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 90px;
  max-width: 90px;
  text-align: center;
}

.status-yes {
  background-color: #069e27;
  color: #fff;
  border-radius: 4px;
}

.status-no {
  background-color: red;
  color: #fff;
  border-radius: 4px;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

.text-center {
  text-align: center;
}

.scrollable-dropdown {
  max-height: 200px;
  overflow-y: auto;
}

/* Focus states */
input:focus,
select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.button-default {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.button-default:hover {
  color: #fff;
  background-color: #0056b3;
  border-color: #004085;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-grid {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .page-search-container {
    width: 100%;
  }

  .search-input,
  .select-distributor {
    min-width: 100%;
  }

  .input-error {
    border-color: #dc3545 !important;
    box-shadow: 0 0 5px rgba(220, 53, 69, 0.4);
  }

  .qty-error-msg {
    display: block;
    color: #dc3545;
    font-size: 11px;
    margin-top: 3px;
  }

  .new-item-btn {
    margin-left: 0;
    width: 100%;
  }
}

/* =========================================================
   DEALER STATEMENT FILTER AREA
   ========================================================= */

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: calc(100vh - 100px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 15px;
  width: 100%;
  align-items: end;
  justify-content: center;
  margin-bottom: 20px;
}

.dropdown-item {
  width: 100%;
  min-width: 0;
  max-width: none;
}

.dropdown-item label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 700;
  color: #212529;
}

.dropdown-item select,
.dropdown-item input[type="date"],
.dropdown-item input[type="text"] {
  width: 100%;
  height: 42px;
  min-height: 42px;
  padding: 8px 10px;
  border: 1px solid #cccccc;
  border-radius: 5px;
  background-color: #ffffff;
  color: #212529;
  font-size: 14px;
  line-height: 1.4;
  box-sizing: border-box;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.dropdown-item select:focus,
.dropdown-item input[type="date"]:focus,
.dropdown-item input[type="text"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.dropdown-item select:disabled,
.dropdown-item input:disabled {
  background-color: #f1f3f5;
  color: #868e96;
  cursor: not-allowed;
  opacity: 1;
}

.search-container {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
}

.search-container .btn {
  width: 100%;
  min-height: 42px;
  margin-top: 0 !important;
}

/* Search button */
.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
  transform: scale(1.02);
}

.btn.button-default:active:not(:disabled) {
  transform: scale(0.98);
}

.btn.button-default:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

/* Export button */
.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
  transform: scale(1.02);
}

.btn.button-export:active:not(:disabled) {
  transform: scale(0.98);
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

/* Preserve pagination dropdown width */
select.page-size-select {
  width: 80px !important;
  height: auto;
  min-height: 0;
  padding: 5px 8px !important;
  display: inline-block !important;
  flex-shrink: 0;
}

/* =========================================================
   RESPONSIVE FILTER LAYOUT
   ========================================================= */

@media (max-width: 1199px) {
  .dropdown-container {
    grid-template-columns: repeat(3, minmax(180px, 1fr));
  }
}

@media (max-width: 991px) {
  .dropdown-container {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 576px) {
  .container {
    margin: 5px;
    padding: 12px;
    width: calc(100% - 10px);
  }

  .dropdown-container {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .dropdown-item {
    width: 100%;
  }

  .search-container .btn {
    width: 100%;
  }
}

/* =========================================================
   PAGINATION — SAME AS PREVIOUS WORKING VERSION
   ========================================================= */

/* Override the global select width for page-size dropdown */
.pagination-bar select.page-size-select {
  width: 80px !important;
  min-width: 80px !important;
  max-width: 80px !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 5px 8px !important;
  display: inline-block !important;
  flex: 0 0 80px !important;
  font-size: 13px;
  line-height: 1.4;
}

/* Keep "Show [20] per page" on one line */
.pagination-bar .page-size-label {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 6px;
  flex-wrap: nowrap !important;
  width: auto !important;
  margin: 0 !important;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 400;
}

/* Main pagination bar */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  width: 100%;
  padding: 8px 0;
  box-sizing: border-box;
}

/* Left, middle and right sections */
.pagination-left,
.pagination-center,
.pagination-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

/* Keep every section from shrinking */
.pagination-left,
.pagination-center,
.pagination-right,
.page-size-label,
.row-info {
  flex-shrink: 0;
}

.pagination-left {
  justify-content: flex-start;
}

.pagination-center {
  justify-content: center;
}

.pagination-right {
  justify-content: flex-end;
  font-size: 13px;
  color: #333333;
}

/* Row-range information */
.row-info {
  font-size: 13px;
  color: #555555;
}

/* Pagination buttons */
.btn-pagination {
  min-width: 34px;
  padding: 5px 10px;
  border: 1px solid #007bff;
  background-color: #ffffff;
  color: #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  transition: background-color 0.2s ease, color 0.2s ease,
    border-color 0.2s ease;
}

.btn-pagination:hover:not(:disabled):not(.ellipsis) {
  background-color: #007bff;
  color: #ffffff;
}

.btn-pagination.active {
  background-color: #007bff;
  color: #ffffff;
  border-color: #007bff;
  font-weight: 600;
  cursor: default;
}

.btn-pagination.ellipsis {
  min-width: auto;
  padding: 5px 4px;
  border: none;
  background-color: transparent;
  color: #888888;
  cursor: default;
}

.btn-pagination:disabled:not(.active) {
  border-color: #dddddd;
  background-color: #fafafa;
  color: #bbbbbb;
  cursor: not-allowed;
}

/* Jump-to-page input */
.pagination-bar .jump-input {
  width: 70px !important;
  min-width: 70px !important;
  max-width: 70px !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 5px 8px !important;
  border: 1px solid #cccccc;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  box-sizing: border-box;
}

.pagination-bar .jump-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.pagination-bar .jump-input:disabled {
  background-color: #f0f0f0;
  color: #888888;
}

/* Go button */
.btn-go {
  min-width: auto;
  padding: 5px 12px;
}

/* Small scrollbar for pagination overflow */
.pagination-bar {
  scrollbar-width: thin;
  scrollbar-color: #c5cbd3 transparent;
}

.pagination-bar::-webkit-scrollbar {
  height: 5px;
}

.pagination-bar::-webkit-scrollbar-track {
  background: transparent;
}

.pagination-bar::-webkit-scrollbar-thumb {
  background-color: #c5cbd3;
  border-radius: 999px;
}

.col-details-cell {
  white-space: pre-line;
}

/* Preserve the old single-row mobile behaviour */
@media (max-width: 768px) {
  .pagination-bar {
    flex-direction: row !important;
    align-items: center;
    justify-content: flex-start;
  }

  .pagination-left,
  .pagination-center,
  .pagination-right {
    justify-content: flex-start;
  }
}

/* =========================================================
   TABLE SCROLL (HORIZONTAL & VERTICAL)
   ========================================================= */
.table-scroll {
  width: 100%;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 550px;
}

/* Normal options always look normal */
.dropdown-item select option {
  background-color: #ffffff;
  color: #212529;
}

/* Inactive dealer: dropdown options */
.dropdown-item select option.inactive-option {
  background-color: #ffebee;
}

/* Select box when an inactive dealer is the selected value */
.dropdown-item select.inactive-select {
  background-color: #ffebee;
}

/* Inactive dealer: table rows */
.table.table-striped > tbody > tr.inactive-row > td {
  --bs-table-accent-bg: transparent;
  --bs-table-bg-type: transparent;
  box-shadow: none !important;
  background-color: #ffebee !important;
}
</style>
